import * as vp from 'vopay';
import { useHistory } from 'react-router-dom';

import type { SupportedModules } from '@/providers/FeatureFlag.provider';
import useActiveModule from '@/hooks/useActiveModule';
import { useNavMenu } from '@/providers/NavMenu.provider';
import { useModuleFeatureFlag } from '@/providers/FeatureFlag.provider';
import useOpenModule from '@/hooks/useOpenModule';
import { usePermissions } from '@/providers/Permissions.provider';
import { usePartnerData } from '@/providers/PartnerData.provider';
import { useBranding } from '@/providers/Branding.provider';
import { useTranslation } from 'react-i18next';

const { Box, NavLeft } = vp;

type NavigationMenuType = {
  expandMenu?: boolean;
};

const NavigationMenu = ({ expandMenu }: NavigationMenuType): React.JSX.Element => {
  const history = useHistory();

  const { getSubModules, onNavClick } = useNavMenu();
  const { activeModule } = useActiveModule();
  const [openModule, setOpenModule] = useOpenModule();
  const { permissions } = usePermissions();
  const { response } = usePartnerData();
  const { secondaryColor } = useBranding();
  const partnerType = response?.partnerType || '';
  const isRoleAdmin = response?.isRoleAdmin || false;
  const isRoleViewOnly = response.isRoleViewOnly;
  const { t } = useTranslation('translations', { keyPrefix: 'UI.NavigationMenu' });

  const supportedModules: { [module in SupportedModules]: vp.NavModuleProps } = {
    dashboard: {
      id: 'navModule-dashboard',
      icon: 'dashboardSmall',
      activeIcon: 'dashboardSmallOn',
      iconWidth: '20px',
      iconHeight: '20px',
      iconColor: 'medium-dark',
      iconColorOn: 'primary-light',
      label: t('overview'),
      isActive: activeModule === 'dashboard',
      isOpen: openModule === 'dashboard',
      subNavModules: getSubModules('dashboard'),
      onClick: () => {
        onNavClick('dashboard');
        setOpenModule('dashboard');
        history.push('/dashboard');
      },
      showNavModule: () => isRoleAdmin || isRoleViewOnly,
    },
    leads: {
      id: 'navModule-leads',
      icon: 'accounts',
      activeIcon: 'accountsOn',
      iconWidth: '20px',
      iconHeight: '20px',
      iconColor: 'medium-dark',
      iconColorOn: 'primary-light',
      label: t('leads'),
      isActive: activeModule === 'leads',
      isOpen: openModule === 'leads',
      subNavModules: getSubModules('leads'),
      onClick: () => {
        onNavClick('leads');
        setOpenModule('leads');
      },
      showNavModule: () => isRoleAdmin || isRoleViewOnly,
    },
    accounts: {
      id: 'navModule-accounts',
      icon: 'briefcase',
      activeIcon: 'briefcaseOn',
      iconWidth: '20px',
      iconHeight: '20px',
      iconColor: 'medium-dark',
      iconColorOn: 'primary-light',
      label: t('myAccounts'),
      isActive: activeModule === 'accounts',
      isOpen: openModule === 'accounts',
      subNavModules: getSubModules('accounts'),
      onClick: () => {
        onNavClick('accounts');
        setOpenModule('accounts');
      },
      showNavModule: () => isRoleAdmin || isRoleViewOnly,
    },
    transactions: {
      id: 'navModule-transactions',
      icon: 'transactions',
      activeIcon: 'transactionsOn',
      iconWidth: '20px',
      iconHeight: '20px',
      iconColor: 'medium-dark',
      iconColorOn: 'primary-light',
      label: t('transactions'),
      isActive: activeModule === 'transactions',
      isOpen: openModule === 'transactions',
      subNavModules: getSubModules('transactions'),
      onClick: () => {
        onNavClick('transactions');
        setOpenModule('transactions');
      },
      showNavModule: () => (isRoleAdmin || isRoleViewOnly) && permissions.accountManagementEnabled,
    },
    billingPackages: {
      id: 'navModule-billingPackages',
      icon: 'wallet',
      activeIcon: 'walletOn',
      iconWidth: '20px',
      iconHeight: '20px',
      iconColor: 'medium-dark',
      iconColorOn: 'primary-light',
      label: t('billingPackages'),
      isActive: activeModule === 'billingPackages',
      isOpen: openModule === 'billingPackages',
      subNavModules: getSubModules('billingPackages'),
      onClick: () => {
        onNavClick('billingPackages');
        setOpenModule('billingPackages');
      },
      showNavModule: () => (isRoleAdmin || isRoleViewOnly) && permissions.accountBillingPackageRequired,
    },
    reports: {
      id: 'navModule-reports-invoice',
      icon: 'reports',
      activeIcon: 'reportsOn',
      iconWidth: '20px',
      iconHeight: '20px',
      iconColor: 'medium-dark',
      iconColorOn: 'primary-light',
      label: t('reports'),
      isActive: activeModule === 'reports',
      isOpen: openModule === 'reports',
      subNavModules: getSubModules('reports'),
      onClick: () => {
        onNavClick('reports');
        setOpenModule('reports');
      },
      showNavModule: () => isRoleAdmin || isRoleViewOnly,
    },
    statements: {
      id: 'navModule-statements',
      icon: 'invoice',
      activeIcon: 'invoiceOn',
      iconWidth: '20px',
      iconHeight: '20px',
      iconColor: 'medium-dark',
      iconColorOn: 'primary-light',
      label: permissions.ShowPartnerIncome ? t('statementsWithPartnerIncome') : t('statements'),
      isActive: activeModule === 'statements',
      isOpen: openModule === 'statements',
      subNavModules: getSubModules('statements'),
      onClick: () => {
        onNavClick('statements');
        setOpenModule('statements');
      },
      showNavModule: () => (isRoleAdmin || isRoleViewOnly) && partnerType !== 'Inside Sales',
    },
    onBoardingFormTracker: {
      id: 'navModule-onboarding-form-tracker',
      icon: 'scheduled',
      activeIcon: 'scheduledOn',
      iconWidth: '20px',
      iconHeight: '20px',
      iconColor: 'medium-dark',
      iconColorOn: 'primary-light',
      label: t('onBoardingFormTracker'),
      isActive: activeModule === 'onBoardingFormTracker',
      isOpen: openModule === 'onBoardingFormTracker',
      subNavModules: getSubModules('onBoardingFormTracker'),
      onClick: () => {
        onNavClick('onBoardingFormTracker');
        setOpenModule('onBoardingFormTracker');
        history.push('/onboarding-form-tracker');
      },
      showNavModule: () => true,
    },
    settings: {
      id: 'navModule-settings',
      icon: 'setting',
      activeIcon: 'settingOn',
      iconWidth: '20px',
      iconHeight: '20px',
      iconColor: 'medium-dark',
      iconColorOn: 'primary-light',
      label: t('settings'),
      isActive: activeModule === 'settings',
      isOpen: openModule === 'settings',
      subNavModules: getSubModules('settings'),
      onClick: () => {
        onNavClick('settings');
        setOpenModule('settings');
        history.push('/settings');
      },
      showNavModule: () => true,
    },

    support: {
      id: 'navModule-support',
      icon: 'support',
      activeIcon: 'supportOn',
      label: t('support'),
      iconWidth: '20px',
      iconHeight: '20px',
      iconColor: 'medium-dark',
      iconColorOn: 'primary-light',
      isActive: activeModule === 'support',
      isOpen: openModule === 'support',
      subNavModules: getSubModules('support'),
      onClick: () => {
        onNavClick('support');
        setOpenModule('support');
      },
      showNavModule: () => true,
    },
    users: {
      id: 'navModule-users',
      icon: 'homeDoor',
      iconColorOn: 'primary-dark',
      iconWidth: '20px',
      iconHeight: '20px',
      label: t('users'),
      isActive: activeModule === 'users',
      isOpen: openModule === 'users',
      subNavModules: getSubModules('users'),
      onClick: () => {
        onNavClick('users');
        setOpenModule('users');
        history.push('/users');
      },
      showNavModule: () => isRoleAdmin || isRoleViewOnly,
    },
  };

  return (
    <Box jc="sa" height="auto" gap="md" flx="col">
      <NavLeft modules={buildModuleList()} expandMenu={expandMenu ?? false} />
    </Box>
  );

  function buildModuleList(): vp.NavModuleProps[] {
    const initList: vp.NavModuleProps[] = [];
    const moduleEntries = (Object.entries(supportedModules) as [SupportedModules, vp.NavModuleProps][]).filter(
      ([, module]) => module.showNavModule(),
    );

    return moduleEntries.reduce((accumulator, [moduleKey, moduleProps]) => {
      const modulePropsCopy = { ...moduleProps };

      if (moduleProps.iconColor) {
        modulePropsCopy.iconColor = vp.getLuminance(secondaryColor) > 0.5 ? 'primary-dark' : 'primary-light';
        modulePropsCopy.iconColorOn = vp.getLuminance(secondaryColor) > 0.5 ? 'primary-dark' : 'primary-light';
        modulePropsCopy.caretColor = vp.getLuminance(secondaryColor) > 0.5 ? 'primary-dark' : 'primary-light';
        modulePropsCopy.caretColorClosed = vp.getLuminance(secondaryColor) > 0.5 ? 'primary-dark' : 'primary-light';
      }

      const { isEnabled } = useModuleFeatureFlag(moduleKey);
      modulePropsCopy.subNavModules = getSubModules(moduleKey);

      return isEnabled ? [...accumulator, modulePropsCopy] : accumulator;
    }, initList);
  }
};

export default NavigationMenu;
